function parseHexColor(hex: string): { r: number; g: number; b: number } | undefined {
  const normalized: string = hex.trim().replace('#', '');
  const full: string =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char: string) => `${char}${char}`)
          .join('')
      : normalized;

  if (!/^[0-9a-fA-F]{6}$/.test(full)) {
    return undefined;
  }

  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16),
  };
}

/** Relative luminance at which white and black text contrast equally (WCAG). */
const READABLE_TEXT_LUMINANCE_THRESHOLD: number = 0.179;

/** Brand buttons: luminance above this uses dark label text. */
const MERCHANT_BUTTON_TEXT_THRESHOLD: number = 0.35;

/**
 * Links / inline merchant text on white: same light-brand gate as button labels.
 * Above this, brand is mixed toward dark for readable text without a background.
 */
const MERCHANT_TEXT_ON_LIGHT_MIX_AMOUNT: number = 0.55;

const MERCHANT_BUTTON_TEXT_DARK: string = '#1a1a1a';
const MERCHANT_BUTTON_TEXT_LIGHT: string = '#ffffff';
const MERCHANT_BUTTON_DISABLED_BG: string = '#d7d7d7';

const BRAND_ICON_FILTER_DARK: string = 'grayscale(1) brightness(0)';
const BRAND_ICON_FILTER_LIGHT: string = 'grayscale(1) brightness(0) invert(1)';

function toLinearChannel(channel: number): number {
  const ratio: number = channel / 255;
  return ratio <= 0.04045 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4;
}

/**
 * Light or dark text, whichever stays readable on the supplied background.
 * Falls back to white when the hex cannot be parsed.
 */
export function readableTextOn(hex: string): string {
  const rgb = parseHexColor(hex);
  if (!rgb) {
    return '#ffffff';
  }

  const luminance: number =
    0.2126 * toLinearChannel(rgb.r)
    + 0.7152 * toLinearChannel(rgb.g)
    + 0.0722 * toLinearChannel(rgb.b);

  return luminance > READABLE_TEXT_LUMINANCE_THRESHOLD ? '#1f2937' : '#ffffff';
}

function relativeLuminance(hex: string): number {
  const rgb = parseHexColor(hex);
  if (!rgb) {
    return 1;
  }

  return (
    0.2126 * toLinearChannel(rgb.r)
    + 0.7152 * toLinearChannel(rgb.g)
    + 0.0722 * toLinearChannel(rgb.b)
  );
}

/** Vuetify theme channels string: "r, g, b" (no rgb() wrapper). */
export function hexToRgbChannels(hex: string): string | undefined {
  const rgb = parseHexColor(hex);
  if (!rgb) {
    return undefined;
  }

  return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
}

/** Text color for a v-btn filled with a merchant brand hex. */
export function getContrastColor(
  hex: string,
  threshold: number = MERCHANT_BUTTON_TEXT_THRESHOLD,
): string {
  return relativeLuminance(hex) > threshold
    ? MERCHANT_BUTTON_TEXT_DARK
    : MERCHANT_BUTTON_TEXT_LIGHT;
}

/** Label color on the unified disabled gray surface (#d7d7d7). */
export function getDisabledButtonTextColor(
  disabledBg: string = MERCHANT_BUTTON_DISABLED_BG,
): string {
  return getContrastColor(disabledBg);
}

/** Marker appended by Lock so contrast CSS can be stripped on branding clear. */
export const CREDIT_CARD_CONTRAST_MARKER: string = '/* authvia-lock-button-contrast */';

/**
 * Append a descendant suffix to every selector in a comma-separated list.
 * `${list} .child` only attaches to the last branch; that zeroed disabled
 * submit buttons when list targeted `.v-btn` and suffix was `.v-btn__overlay`.
 */
export function appendToEachCssSelector(selectorList: string, suffix: string): string {
  const trimmedSuffix: string = suffix.trim();
  return selectorList
    .split(',')
    .map((selector: string) => `${selector.trim()} ${trimmedSuffix}`)
    .join(',\n');
}

/**
 * Plain CSS for authvia-credit-card `data-vuetify-overrides`.
 * Forwarded through payment-method-create → authvia-credit-card-wrapper → CDN iframe.
 */
export function buildCreditCardVuetifyOverrides(brandColor: string): string {
  const onColor: string = getContrastColor(brandColor);
  const textOnLight: string = getMerchantTextOnLight(brandColor);
  const disabledTextColor: string = getDisabledButtonTextColor();
  const submitBtn: string =
    '.authvia-credit-card-form__root .v-btn.v-btn--variant-elevated.authvia-credit-card-form__submit';
  const disabledBtn: string = [
    '.credit-card-form-spacing .avwc-authvia-credit-card-form__root .v-btn.v-btn--disabled:not(.v-btn--loading):not(.v-btn-toggle .v-btn)',
    '.authvia-credit-card-form__root .v-btn.v-btn--disabled:not(.v-btn--loading):not(.v-btn-toggle .v-btn)',
    `${submitBtn}.v-btn--disabled:not(.v-btn--loading)`,
  ].join(',\n');
  const disabledBtnContent: string = appendToEachCssSelector(disabledBtn, '.v-btn__content');
  const disabledBtnOverlay: string = appendToEachCssSelector(disabledBtn, '.v-btn__overlay');
  const legalLinks: string = [
    '.authvia-credit-card-form__root a[href*="privacy"]',
    '.authvia-credit-card-form__root a[href*="terms"]',
    '.authvia-form-with-terms a',
    '.authvia-form-with-terms__caption a',
  ].join(',\n');
  const manualAddressBtn: string =
    '.addr-manual-button, .manual-entry-btn, .authvia-address-form__root .addr-manual-button';

  return `${CREDIT_CARD_CONTRAST_MARKER}
.authvia-credit-card-form__root {
  --button-text: ${onColor} !important;
  --button-disabled-color: ${MERCHANT_BUTTON_DISABLED_BG} !important;
}
${submitBtn}:not(.v-btn--disabled),
${submitBtn}:not(.v-btn--disabled) .v-btn__content {
  color: ${onColor} !important;
}
${disabledBtn} {
  background: ${MERCHANT_BUTTON_DISABLED_BG} !important;
  background-color: ${MERCHANT_BUTTON_DISABLED_BG} !important;
  color: ${disabledTextColor} !important;
  opacity: 1 !important;
  box-shadow: none !important;
}
${disabledBtnContent} {
  color: ${disabledTextColor} !important;
  opacity: 1 !important;
}
${disabledBtnOverlay} {
  opacity: 0 !important;
}
${legalLinks} {
  color: ${textOnLight} !important;
}
${manualAddressBtn} {
  color: #333 !important;
}`.trim();
}

const AVWC_BRAND_COLOR_VAR: string = '--avwc-color';
const MERCHANT_BRAND_COLOR_VAR: string = '--merchant-color';

/** Treat CSS defaults / cleared branding as inactive, not a live merchant color. */
export function normalizeBrandColor(value: string | undefined): string | undefined {
  const trimmed: string | undefined = value?.trim();
  if (!trimmed || trimmed.toLowerCase() === 'transparent') {
    return undefined;
  }

  return parseHexColor(trimmed) ? trimmed : undefined;
}

function readCssVar(root: HTMLElement, name: string): string | undefined {
  const inline: string = root.style.getPropertyValue(name).trim();
  if (inline) {
    return inline;
  }

  const computed: string = getComputedStyle(root).getPropertyValue(name).trim();
  return computed || undefined;
}

/** Read --avwc-color from inline style or computed custom properties on one root. */
export function readAvwcBrandColor(root: HTMLElement): string | undefined {
  return normalizeBrandColor(readCssVar(root, AVWC_BRAND_COLOR_VAR));
}

/**
 * Active merchant brand hex from Lock inline branding only.
 * Ignores :root CSS defaults (e.g. #43A047) after wallet pages clear inline --merchant-color.
 */
export function readMerchantBrandColor(
  root: HTMLElement = document.documentElement,
): string | undefined {
  const inlineMerchant: string | undefined = normalizeBrandColor(
    root.style.getPropertyValue(MERCHANT_BRAND_COLOR_VAR),
  );
  if (inlineMerchant) {
    return inlineMerchant;
  }

  return readAvwcBrandColor(root);
}

export function syncPaymentMethodDialogPortalTokens(portal: HTMLElement): void {
  const brandColor: string | undefined = readMerchantBrandColor();
  if (!brandColor) {
    clearContrastButtonTokens(portal.style);
    return;
  }

  applyContrastButtonTokens(portal.style, brandColor);
}

/** Merchant-filled buttons across Lock and Authvia packages. */
export function applyContrastButtonTokens(
  style: CSSStyleDeclaration,
  brandColor: string,
): string {
  const onColor: string = getContrastColor(brandColor);
  const disabledTextColor: string = getDisabledButtonTextColor();
  const onChannels: string | undefined = hexToRgbChannels(onColor);
  const primaryChannels: string | undefined = hexToRgbChannels(brandColor);

  const brandIconFilter: string =
    onColor === MERCHANT_BUTTON_TEXT_DARK
      ? BRAND_ICON_FILTER_DARK
      : BRAND_ICON_FILTER_LIGHT;

  style.setProperty('--merchant-on-color', onColor, 'important');
  style.setProperty('--button-text', onColor, 'important');
  style.setProperty('--action-btn-text-color', onColor, 'important');
  style.setProperty('--web-component-btn-text-color', onColor, 'important');
  style.setProperty('--action-btn-disabled-color', MERCHANT_BUTTON_DISABLED_BG, 'important');
  style.setProperty('--button-disabled-color', MERCHANT_BUTTON_DISABLED_BG, 'important');
  style.setProperty('--web-component-btn-disabled-color', MERCHANT_BUTTON_DISABLED_BG, 'important');
  style.setProperty('--action-btn-disabled-text-color', disabledTextColor, 'important');
  style.setProperty('--button-brand-icon-filter', brandIconFilter, 'important');
  style.setProperty('--button-disabled-brand-icon-filter', BRAND_ICON_FILTER_DARK, 'important');

  if (onChannels) {
    style.setProperty('--v-theme-on-primary', onChannels, 'important');
  }
  if (primaryChannels) {
    style.setProperty('--v-theme-primary', primaryChannels, 'important');
  }

  return onColor;
}

export function clearContrastButtonTokens(style: CSSStyleDeclaration): void {
  style.removeProperty('--merchant-on-color');
  style.removeProperty('--button-text');
  style.removeProperty('--action-btn-text-color');
  style.removeProperty('--web-component-btn-text-color');
  style.removeProperty('--action-btn-disabled-color');
  style.removeProperty('--button-disabled-color');
  style.removeProperty('--web-component-btn-disabled-color');
  style.removeProperty('--action-btn-disabled-text-color');
  style.removeProperty('--button-brand-icon-filter');
  style.removeProperty('--button-disabled-brand-icon-filter');
  style.removeProperty('--v-theme-primary');
  style.removeProperty('--v-theme-on-primary');
}

/**
 * Readable merchant-colored links / inline text on a white surface.
 * Light brands get a same-hue darker mix; dark brands keep the brand hex.
 * Does not affect button label tokens (--merchant-on-color, etc.).
 */
export function getMerchantTextOnLight(hex: string): string {
  if (relativeLuminance(hex) > MERCHANT_BUTTON_TEXT_THRESHOLD) {
    return mixHexWithDark(hex, MERCHANT_TEXT_ON_LIGHT_MIX_AMOUNT);
  }

  return hex.trim();
}

/** Set --merchant-text-on-light for links / inline text on white. */
export function applyMerchantTextOnLightToken(
  style: CSSStyleDeclaration,
  brandColor: string,
): string {
  const textOnLight: string = getMerchantTextOnLight(brandColor);
  style.setProperty('--merchant-text-on-light', textOnLight, 'important');
  return textOnLight;
}

export function clearMerchantTextOnLightToken(style: CSSStyleDeclaration): void {
  style.removeProperty('--merchant-text-on-light');
}

/**
 * Authvia WCs set --avwc-color on attach roots but leave Vuetify on-primary white.
 * Mirror contrast tokens on attach roots for PM dialog buttons (Continue, etc.).
 */
export function syncAvwcContrastTokens(root: HTMLElement): boolean {
  const brandColor: string | undefined = readAvwcBrandColor(root);
  if (!brandColor) {
    clearContrastButtonTokens(root.style);
    return false;
  }

  applyContrastButtonTokens(root.style, brandColor);

  return true;
}

export function syncAllAvwcContrastTokens(): void {
  document
    .querySelectorAll(
      [
        '.authvia-pm-dialog-attach',
        '[data-testid="payment-method-dialog-attach"]',
        '.authvia-customer-authentication',
        '.avwc-customer-authentication__root',
        '.avwc-customer-authentication__address-root',
        '.avwc-customer-authentication__otp-root',
        '.avwc-customer-authentication__sending-root',
      ].join(', '),
    )
    .forEach((node: Element) => syncAvwcContrastTokens(node as HTMLElement));
}

/** BP action cards, create-transaction, and auth WC roots mount after branding. */
export function syncAllActionContrastRoots(): void {
  const merchantBrand: string | undefined = readMerchantBrandColor();

  document
    .querySelectorAll(
      [
        '.action-card',
        '.authvia-create-transaction__root',
        '.authvia-customer-authentication',
        '.avwc-customer-authentication__root',
        '.avwc-customer-authentication__address-root',
        '.avwc-customer-authentication__otp-root',
        '.avwc-customer-authentication__sending-root',
      ].join(', '),
    )
    .forEach((node: Element) => {
      const el: HTMLElement = node as HTMLElement;
      const brandColor: string | undefined =
        readAvwcBrandColor(el) ?? merchantBrand;
      if (!brandColor) {
        clearContrastButtonTokens(el.style);
        return;
      }

      applyContrastButtonTokens(el.style, brandColor);
    });
}

let actionContrastSyncScheduled: boolean = false;

export function scheduleActionContrastRootsSync(): void {
  if (actionContrastSyncScheduled) {
    return;
  }

  actionContrastSyncScheduled = true;
  requestAnimationFrame(() => {
    actionContrastSyncScheduled = false;
    syncAllActionContrastRoots();
  });
}

export function startActionContrastRootsObserver(): void {
  scheduleActionContrastRootsSync();

  const observer = new MutationObserver(() => {
    scheduleActionContrastRootsSync();
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

/**
 * Opaque mix of a primary hex onto white (keeps dark header text readable on scroll).
 * @param amount 0–1 share of the primary color
 */
export function mixHexWithWhite(hex: string, amount: number): string {
  const rgb = parseHexColor(hex);
  if (!rgb) {
    return '#ffffff';
  }

  const weight: number = Math.min(Math.max(amount, 0), 1);
  const mix = (channel: number): number =>
    Math.round(255 * (1 - weight) + channel * weight);

  const toHex = (value: number): string =>
    value.toString(16).padStart(2, '0');

  return `#${toHex(mix(rgb.r))}${toHex(mix(rgb.g))}${toHex(mix(rgb.b))}`;
}

/**
 * Opaque mix of a primary hex toward #1a1a1a (CSS color-mix equivalent for text on white).
 * @param amount 0–1 share of the primary color (e.g. 0.55 → 55% brand, 45% dark)
 */
export function mixHexWithDark(hex: string, amount: number): string {
  const rgb = parseHexColor(hex);
  if (!rgb) {
    return MERCHANT_BUTTON_TEXT_DARK;
  }

  const darkRgb = parseHexColor(MERCHANT_BUTTON_TEXT_DARK);
  if (!darkRgb) {
    return MERCHANT_BUTTON_TEXT_DARK;
  }

  const weight: number = Math.min(Math.max(amount, 0), 1);
  const mix = (channel: number, darkChannel: number): number =>
    Math.round(channel * weight + darkChannel * (1 - weight));

  const toHex = (value: number): string =>
    value.toString(16).padStart(2, '0');

  return `#${toHex(mix(rgb.r, darkRgb.r))}${toHex(mix(rgb.g, darkRgb.g))}${toHex(mix(rgb.b, darkRgb.b))}`;
}
